import {
  ErrorOutline as ErrorIcon,
  InfoOutlined as InfoIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material'
import { Box, Button, Card, CardContent, Stack, Typography, useTheme } from '@mui/material'
import PropTypes from 'prop-types'
import { getDashboardCardStyles } from '@/features/shared-ui'

const SEVERITY_ICONS = { info: InfoIcon, warning: WarningIcon, error: ErrorIcon }

/**
 * Full-page notice shown instead of the app when the user cannot proceed:
 * no access, or a permission lookup that failed. One neutral card for every
 * state; severity changes only the icon so the notices read as a set.
 */
export function AccessGateMessage({ severity = 'info', title, message, actions = [] }) {
  const theme = useTheme()
  const cardStyles = getDashboardCardStyles(theme)
  const Icon = SEVERITY_ICONS[severity]

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Card sx={{ ...cardStyles.root, width: '100%', maxWidth: 600, height: 'auto' }}>
        <CardContent sx={{ ...cardStyles.content, p: 4 }}>
          <Stack direction='row' spacing={2} alignItems='flex-start'>
            <Icon sx={{ color: theme.palette[severity].main, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant='h5' sx={{ ...cardStyles.title, fontWeight: 600 }}>
                {title}
              </Typography>
              <Typography variant='body1' color='text.secondary' sx={{ mt: 1, mb: 3 }}>
                {message}
              </Typography>
              <Stack direction='row' spacing={1}>
                {actions.map(({ label, onClick, variant = 'outlined' }) => (
                  <Button key={label} variant={variant} onClick={onClick}>
                    {label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}

AccessGateMessage.propTypes = {
  severity: PropTypes.oneOf(['info', 'warning', 'error']),
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.string,
    })
  ),
}
